import type {ImageFileConstraints} from '@/shared/application/validators/image-file.validator';

export type Dimensions = {
  readonly width: number;
  readonly height: number;
};

export type ExifOrientation = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export type SniffedImageFormat = 'jpeg' | 'png' | 'webp' | 'gif' | 'bmp' | 'heic' | 'avif';

export type WebpFlavor = 'lossy' | 'lossless';

/** What the magic bytes say about a file; `File.type` is never consulted. */
export type SniffedImage = {
  readonly format: SniffedImageFormat;
  readonly mimeType: string;
  /** Only meaningful for WebP; `null` when the container did not say. */
  readonly webpFlavor: WebpFlavor | null;
  readonly hasAlpha: boolean;
  /** WebP VP8X advertises EXIF in its flags; such files may carry an orientation we do not parse. */
  readonly hasExifFlag: boolean;
};

export type ImageContentKind = 'photo' | 'graphic';

export type ImageContentHint = 'auto' | ImageContentKind;

/** A region in oriented source pixels. */
export type CropRegion = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

export const UPLOAD_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

export type UploadImageMimeType = (typeof UPLOAD_IMAGE_MIME_TYPES)[number];

export type OutputImageFormat = 'image/jpeg' | 'image/png';

export type OutputLadderStep = {
  readonly format: OutputImageFormat;
  readonly quality?: number;
  /** JPEG has no alpha: composite transparent pixels over white first. */
  readonly flattenOnWhite: boolean;
};

/** An ImageData-like tile sampled from the decoded image. */
export type PixelSample = {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
};

export type PreprocessImageOptions = {
  /** Destination limits; feature-owned values that mirror the server. */
  readonly constraints: ImageFileConstraints;
  /** Defaults to `LLM_IMAGE_MAX_LONG_EDGE_PX`. */
  readonly maxLongEdgePx?: number;
  /** Defaults to `'auto'` (source-format prior refined by pixel statistics). */
  readonly contentHint?: ImageContentHint;
  /** Crop before resizing when the product already knows the useful region. */
  readonly crop?: CropRegion;
  readonly signal?: AbortSignal;
};

export type ImagePreprocessErrorCode =
  | 'unreadable'
  | 'unsupported-format'
  | 'input-too-large'
  | 'too-many-pixels'
  | 'decode-failed'
  | 'encode-failed'
  | 'output-too-large'
  | 'aborted';

export type PreparedImage =
  | {
      readonly disposition: 'processed';
      readonly file: File;
      readonly format: OutputImageFormat;
      readonly kind: ImageContentKind;
      readonly width: number;
      readonly height: number;
      readonly sourceWidth: number;
      readonly sourceHeight: number;
    }
  | {
      readonly disposition: 'passed-through';
      readonly file: File;
      readonly format: UploadImageMimeType;
      readonly kind: ImageContentKind;
      readonly width: number;
      readonly height: number;
    }
  | {
      readonly disposition: 'fallback';
      readonly file: File;
      readonly reason: ImagePreprocessErrorCode;
    };
